import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleName, MagnifyingGlassGold } from '@/resources/assets/images';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { dashboardGetGooglePlaces } from '@/modules/dashboard/dashboardActions';
import Loader, { Status } from '@/components/Loader/Loader';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import './SearchTabLocationSearchBar.scss';
import type { BusinessQuery } from '@/modules/dashboard/dashboardTypes';
import { useUserData } from '@/modules/lists/details/src/hooks/useUserData';

interface Props {
	setSelectedLocation: (filter: string) => void;
	onFilterChange: (query: BusinessQuery) => void;
	onGetBusiness: (query: BusinessQuery) => void;
}

const SearchTabLocationSearchBar = ({
	setSelectedLocation,
	onFilterChange,
	onGetBusiness
}: Props) => {
	const [googlePlaces, googlePlacesFailure, googlePlacesLoading, query] =
		useSelector((state: RootState) => [
			state.dashboard.getIn(['googlePlaces', 'data']).toJS(),
			state.dashboard.getIn(['googlePlaces', 'failure']),
			state.dashboard.getIn(['googlePlaces', 'loading']),
			state.dashboard.get('query').toJS()
		]);

	const [searchBarValue, setSearchBarValue] = useState<string>('');
	const [hideSearchBarDropdown, setHideSearchBarDropdown] =
		useState<boolean>(false);
	const [initialSearchBarDropdownRender, setInitialSearchBarDropdownRender] =
		useState<boolean>(false);

	const dispatch = useDispatch();
	const getGooglePlaces = useCallback(
		(searchString: string) => {
			dispatch(dashboardGetGooglePlaces({ searchString }));
		},
		[dispatch]
	);

	const searchBarDivRef = useRef<HTMLDivElement>(null);
	const searchBarDropdownRef = useRef<HTMLDivElement>(null);

	const searchBarOnFocusHandler = () => {
		if (!initialSearchBarDropdownRender) {
			setInitialSearchBarDropdownRender(true);
		}

		setHideSearchBarDropdown(false);
	};
	const { user } = useUserData();

	const addLocationFilter = (lat: number, lng: number) => {
		const newQuery = { ...query };

		// Remove existing location filters to avoid conflicts
		delete newQuery.state;
		delete newQuery.country;

		newQuery.coordinates = `[${lng}, ${lat}]`;
		newQuery.skip = 0;

		if (newQuery.coordinates) {
			// Remove existing location filters to avoid conflicts
			delete newQuery.state;
			delete newQuery.country;
		}

		newQuery.sortField = 'score';
		newQuery.sort = { score: -1 };
		
		onFilterChange(newQuery);
		onGetBusiness(newQuery);
	};

	const handleClickSuggestion = (suggestion: string) => {
		setHideSearchBarDropdown(true);
		setSelectedLocation(suggestion);
		setSearchBarValue('');
	};

	const searchBarOnKeyDownHandler = (
		e: React.KeyboardEvent<HTMLInputElement>
	) => {
		if (e.key === 'Enter') {
			setHideSearchBarDropdown(true);

			// Ensure there are valid googlePlaces results
			if (googlePlaces.length > 0) {
				const selectedPlace = googlePlaces[0];

				// Check if the selected place has formatted_address and geometry
				if (
					selectedPlace?.formatted_address &&
					selectedPlace?.geometry?.location
				) {
					const { lat, lng } = selectedPlace.geometry.location;

					// Update the selected location and coordinates
					setSelectedLocation(selectedPlace.formatted_address);
					addLocationFilter(lat, lng);
					setSearchBarValue('');
				} else {
					console.warn('No valid location data found.');
				}
			} else {
				console.warn('No suggested locations available.');
			}
		}
	};

	const closeSuggestionsDropdown = e => {
		if (
			!searchBarDivRef?.current?.contains(e.target) &&
			!searchBarDropdownRef?.current?.contains(e.target) &&
			!hideSearchBarDropdown
		) {
			setHideSearchBarDropdown(true);
		}
	};

	useEffect(() => {
		window.addEventListener('click', closeSuggestionsDropdown);

		return () =>
			window.removeEventListener('click', closeSuggestionsDropdown);
	}, []);

	useEffect(() => {
		if (googlePlacesFailure) {
			setHideSearchBarDropdown(true);
		}
	}, [googlePlacesFailure]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debounceFn = useCallback(debounce(getGooglePlaces, 500), [
		getGooglePlaces
	]);

	const searchBarOnChangeHandler = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const newValue = e.target.value;

		setSearchBarValue(newValue);

		if (newValue.length === 0) return null;
		debounceFn(newValue);
	};

	return (
		<div
			ref={searchBarDivRef}
			className="search-tab-filters-list-item-dropdown-search-bar"
		>
			<MagnifyingGlassGold className="search-tab-filters-list-item-dropdown-search-bar-icon" />
			<input
				className="search-tab-filters-list-item-dropdown-search-bar-input"
				type="text"
				placeholder="Enter target market address"
				value={searchBarValue}
				onChange={searchBarOnChangeHandler}
				onFocus={searchBarOnFocusHandler}
				onKeyDown={searchBarOnKeyDownHandler}
			/>
			{googlePlacesFailure && (
				<div className="search-tab-filters-list-item-dropdown-search-failed">
					Oops! We're unable to pull locations right now. Please try
					again.
				</div>
			)}
			{!hideSearchBarDropdown &&
				initialSearchBarDropdownRender &&
				searchBarValue?.length > 0 && (
					<div
						ref={searchBarDropdownRef}
						className="search-tab-filters-list-item-dropdown-search-bar-dropdown"
					>
						<div className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item google">
							Suggested Locations
							<GoogleName className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item-google-icon" />
						</div>
						{googlePlacesLoading && (
							<div className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item loading">
								<Loader
									className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item-loader"
									status={Status.LOADING}
								/>
							</div>
						)}
						{isEmpty(googlePlaces) && !googlePlacesLoading && (
							<>
								<div className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item no-results">
									No results found
								</div>
								<div className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item support">
									Is your location not showing up?
									<br />
									Contact{' '}
									<a href="mailto:support@re2.ai">
										support@re2.ai
									</a>
								</div>
							</>
						)}
						{googlePlaces.map(
							(place: google.maps.places.PlaceResult) => (
								<div
									key={place.place_id}
									className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item"
									onClick={() => {
										const { lat, lng } =
											place?.geometry?.location;
										handleClickSuggestion(
											place.formatted_address as string
										);

										addLocationFilter(lat, lng);
									}}
								>
									{place.formatted_address}
								</div>
							)
						)}
					</div>
				)}
		</div>
	);
};

export default SearchTabLocationSearchBar;
